import { 
  Card, 
  Stack, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Skeleton, 
  Fade, 
  Chip, 
  IconButton, 
  Tooltip 
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Page from "../Page";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { useUsers } from "src/hooks/useUsers.hook";
import { BaseTable } from "../BaseTable/BaseTable";
import { userListColumns } from "./user.constants";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import personFill from "@iconify/icons-eva/person-fill";
import emailFill from "@iconify/icons-eva/email-fill";
import calendarFill from "@iconify/icons-eva/calendar-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

export default function UserList() {
  const navigate = useNavigate()
  const hasCreateAccess = useAccess(FEATURE.USERS, "C");
  const isGrandParent = useIsGrandParent();
  const isRootSuperAdmin = useIsRootSuperAdmin();
  // Mirrors restrictGrandParentUserAdminToRoot on the API: inside the Grand
  // Parent company a new user IS another super admin, so only the ROOT owner may
  // create one. Offering the button to anyone else just produces a 403.
  const canCreateUser = hasCreateAccess && (!isGrandParent || isRootSuperAdmin);
  const isReadOnly = useAccess(FEATURE.USERS, "R");
  const { data: users, isFetching } = useUsers();
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  
  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/users/${id}`);
  };

  // Calculate statistics
  const totalUsers = users?.length || 0;
  const recentUsers = users?.filter(user => {
    const userDate = new Date(user.createDate);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return userDate >= thirtyDaysAgo;
  }).length || 0;

  useEffect(() => {
    if (!isFetching){
      let data = [...users];
      setTableData(data);
    }
  }, [isFetching]);

  const handleSearching = (event) => {
    const dataToSearchFrom = users;
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);
    
    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "username",
            "firstName",
            "lastName",
            "email",
            "createDate",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "createDate"
                ? yearFirstDateTimeFormat(value).toString()
                : value ? value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData([...users]);
    }
  };

  const handleRefresh = () => {
    // Trigger refetch if needed
    window.location.reload();
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box>
      <Stack direction="row" spacing={3} mb={4}>
        {[1, 2].map((item) => (
          <Skeleton key={item} variant="rectangular" width="100%" height={120} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
      <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
    </Box>
  );

  // Empty state component
  const EmptyState = () => (
    <Fade in={true}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Icon icon={personFill} width={60} height={60} color="#9e9e9e" />
        </Box>
        <Typography variant="h5" gutterBottom color="text.secondary">
          {searchValue ? 'No users found' : 'No users yet'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
          {searchValue 
            ? `No users match your search "${searchValue}". Try adjusting your search terms.`
            : 'Get started by adding your first CMS system user to manage access and permissions.'
          }
        </Typography>
      </Box>
    </Fade>
  );

  return (
    <Page
      title="CMS System Users"
      {...(canCreateUser && { path: `/users/new` })}
      actionButtonText="Add CMS System User"
    >
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
              CMS System Users
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Manage system users, roles, and access permissions
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <StyledListCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                    {isFetching ? <Skeleton width={40} /> : totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon={personFill} width={32} height={32} color="white" />
                </Box>
              </Stack>
            </StyledListCard>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <StyledListCard>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                    {isFetching ? <Skeleton width={40} /> : recentUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent Users (30 days)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon={calendarFill} width={32} height={32} color="white" />
                </Box>
              </Stack>
            </StyledListCard>
          </Grid>
        </Grid>
      </Box>

      {/* Search and Filters Section */}
      <StyledListCard sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Search & Filter
          </Typography>
          {searchValue && (
            <Chip
              label={`${tableData?.length || 0} results`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Stack>
        <CommonSearchbar
          placeholder="Search users by name, email, or username..."
          searchFunc={(e) => handleSearching(e)}
        />
      </StyledListCard>

      {/* Data Table */}
      <StyledListCard>
        {isFetching ? (
          <LoadingSkeleton />
        ) : (!tableData || tableData.length === 0) ? (
          <EmptyState />
        ) : (
          <BaseTable
            rows={tableData}
            columns={userListColumns(isReadOnly)}
            loading={isFetching}
            getRowId={(row) => row.userId}
            pageSize={10}
            isReadOnly={isReadOnly}
            showExportCsvBtn={true}
            csvMorphemes="cms_system_users"
            csvColumns={userListColumns(isReadOnly)}
            rowOrCellClickRedirect={rowOrCellClickRedirect}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: isReadOnly ? 'default' : 'pointer',
                '&:hover': {
                  backgroundColor: isReadOnly ? 'transparent' : 'action.hover',
                },
              },
              '& .MuiDataGrid-cell': {
                cursor: isReadOnly ? 'default' : 'pointer',
              },
              '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
                borderBottom: isReadOnly ? 'none' : '1px solid',
                borderBottomColor: 'primary.light',
              },
            }}
          />
        )}
      </StyledListCard>
    </Page>
  );
}
